
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import InventoryTypeFieldModal from '@/components/modals/InventoryTypeFieldModal.vue'
import { InventoryTypeDetails } from '@/@types/responses/inventory_type/InventoryTypeDetails'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { InventoryTypeService } from '@/services/inventoryType.service'
import { InventoryTypeField } from '@/@types/responses/inventory_type/InventoryTypeField'
import { InventoryTypeFieldService } from '@/services/inventoryTypeField.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { InventoryTypeMessage } from '@/@types/messages/InventoryTypeMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventoryType',
  components: {
    AuditInformation,
    InventoryTypeFieldModal,
    LoadingPulse,
    LockingButton,
    NumericInput,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showInventoryTypeFieldModal) {
      this.showInventoryTypeFieldModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const inventoryTypeFieldService = inject(PROVIDER_CONSTANTS.inventoryTypeFieldService) as InventoryTypeFieldService
    const inventoryTypeService = inject(PROVIDER_CONSTANTS.inventoryTypeService) as InventoryTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      inventoryTypeFieldService,
      inventoryTypeService,
      toast
    }
  },
  data() {
    let inventoryType: InventoryTypeDetails|undefined
    let inventoryTypeFieldToEdit: InventoryTypeField|undefined
    let unchangedInventoryType: InventoryTypeDetails|undefined

    return {
      inventoryType,
      inventoryTypeFieldToEdit,
      loading: true,
      saving: false,
      showInventoryTypeFieldModal: false,
      unchangedInventoryType
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.inventoryType
      const b = this.unchangedInventoryType

      return (a?.name !== b?.name || a?.displayOrder !== b?.displayOrder) && (a?.name.trim() !== '' && a?.displayOrder !== undefined && a?.displayOrder >= 0)
    },
    titleText(): string {
      return !this.inventoryType || this.inventoryType.id === 0 ? 'Create Inventory Type' : 'Edit Inventory Type'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.getInventoryType()
    } else {
      this.inventoryType = new InventoryTypeDetails(0)
      this.unchangedInventoryType = new InventoryTypeDetails(0)
      this.loading = false
    }
  },
  methods: {
    boolToString(bool: boolean): string {
      return bool ? 'Yes' : 'No'
    },
    getInventoryType(): Promise<void> {
      return this.inventoryTypeService.getInventoryType(this.inventoryType ? this.inventoryType.id : this.id).then(inventoryType => {
        this.inventoryType = inventoryType
        this.unchangedInventoryType = JSON.parse(JSON.stringify(inventoryType))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving  = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the inventory type. Try reloading the page.')
        }
      })
    },
    onAddFieldClicked(): void {
      if (!this.inventoryType) return

      let displayOrder = 1
      if (this.inventoryType.inventoryTypeFields.length > 0) {
        const currentMaxValue = this.inventoryType.inventoryTypeFields[this.inventoryType.inventoryTypeFields.length - 1].displayOrder
        displayOrder = currentMaxValue + 1
      }

      this.inventoryTypeFieldToEdit = new InventoryTypeField(0, displayOrder)
      this.showInventoryTypeFieldModal = true
    },
    onDeleteClicked(id: number): void {
      this.inventoryTypeFieldService.deleteInventoryTypeField(id).then(() => {
        this.getInventoryType().then(() => {
          this.toast.success('Field deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the field. Please try again.')
        }
      })
    },
    onSaveInventoryTypeClicked(): void {
      if (!this.inventoryType) return

      this.saving = true
      
      const inventoryTypeMessage = new InventoryTypeMessage(this.inventoryType.displayOrder, this.inventoryType.name)
      if (this.inventoryType.id === 0)
        this.inventoryTypeService.createInventoryType(inventoryTypeMessage).then(objectCreatedResponse => {
          if (!this.inventoryType) return

          this.inventoryType.id = objectCreatedResponse.id
          this.getInventoryType().then(() => {
            this.$router.replace(`/inventory-type/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The inventory type was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the inventory type. Please try again.')
          }
        })
      else
        this.inventoryTypeService.updateInventoryType(this.id, inventoryTypeMessage).then(() => {
          this.toast.success('The inventory type was updated successfully.')
          this.saving = false
          this.$router.push('/inventory-types')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the inventory type. Please try again.')
          }
        })
    },
    onTableRowClicked(inventoryTypeField: InventoryTypeField): void {
      this.inventoryTypeFieldToEdit = inventoryTypeField
      this.showInventoryTypeFieldModal = true
    },
    typeToDisplayValue(type: number): string {
      switch(type) {
        case 1: return 'Manufacturer'
        case 2: return 'Supplier'
        case 3: return 'Free-Form Text'
        case 4: return 'Selection'
        case 5: return 'Yes / No'
        case 6: return 'Number'
        case 7: return 'Build Step'
        case 8: return 'Caliber'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.inventoryType)
        document.title = `${this.inventoryType.name} - Short Action Customs, LLC`
    }
  }
})
